"use client";

import { usePerpInstrumentName } from "#/hooks/usePerpInstrumentName";
import { useThrottle } from "#/hooks/useThrottle";
import { useIndexPrices } from "#/store/app/indexPrices/indexPrices";
import { useMarket } from "#/store/app/portfolio/market";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import {
  type CalculateTradeFeeParams,
  type TradeFee,
  type TradeFeeProps,
} from "#/types/fees";
import { FeeCalculator } from "#/utils/feeCalculator";
import { getPerpInstrumentProperties } from "#/utils/instrument.utils";

import { useEstimatedExecPriceController } from "../EstimatedExecPrice/EstimatedExecPrice.controller";

export const calculateTradeFees = ({
  amount,
  optionPrice,
  underlying,
  isPerp,
  indexPrice,
}: CalculateTradeFeeParams): TradeFee => {
  const tradingFee = FeeCalculator.calculateTradingFee(
    indexPrice,
    optionPrice ?? null,
    underlying,
    isPerp,
    amount,
    false,
  );

  const systemicFee = FeeCalculator.calculateSystemicFee(
    indexPrice,
    isPerp,
    amount,
  );

  return { tradingFee, systemicFee, totalFee: tradingFee + systemicFee };
};

const useCalcOptionsTradeFee = ({
  instrumentType,
}: TradeFeeProps): TradeFee => {
  const { option } = useOption();
  const { optionSizeNum } = useOptionSize();
  const { indexPrices } = useIndexPrices();
  const { market } = useMarket();
  const indexPrice = useThrottle(
    indexPrices[option?.underlying ?? market.underlying],
    60_000,
  );
  const { executionPrice } = useEstimatedExecPriceController({
    type: "market",
    kind: "options",
  });

  if (!option || !executionPrice || instrumentType !== "options")
    return {
      totalFee: NaN,
      tradingFee: NaN,
      systemicFee: NaN,
    };

  const fees = calculateTradeFees({
    amount: optionSizeNum,
    optionPrice: executionPrice,
    underlying: option?.underlying,
    isPerp: false,
    indexPrice,
  });

  return fees;
};

const useCalcPerpsTradeFee = ({ instrumentType }: TradeFeeProps): TradeFee => {
  const { perpInstrumentName } = usePerpInstrumentName();
  const { perpSizeNum } = usePerpSize();
  const { perpLimitPrice } = usePerpLimitPrice();
  const { underlying } = getPerpInstrumentProperties(perpInstrumentName);
  const { indexPrices } = useIndexPrices();
  const indexPrice = useThrottle(indexPrices[underlying], 60_000);

  if (!perpSizeNum || !perpLimitPrice || instrumentType !== "perps")
    return {
      totalFee: NaN,
      tradingFee: NaN,
      systemicFee: NaN,
    };

  const fees = calculateTradeFees({
    amount: perpSizeNum,
    underlying,
    isPerp: true,
    indexPrice,
  });
  return fees;
};

export const useTradeFeeController = ({
  instrumentType,
}: TradeFeeProps): TradeFee => {
  const optionTradeFees = useCalcOptionsTradeFee({ instrumentType });
  const perpTradeFees = useCalcPerpsTradeFee({ instrumentType });

  const fees = instrumentType === "options" ? optionTradeFees : perpTradeFees;

  return fees;
};
